export class SelectionPreviewRenderer {
  private appState: any

  constructor(appState?: any) {
    this.appState = appState
  }

  /**
   * Render preview HTML for a selection
   */
  public renderSelectionPreview(selection: any): string {
    // Debug: Check the detection logic and extracted text
    console.log('🔍 SelectionPreviewRenderer: Rendering selection:', {
      id: selection.id,
      document_type: selection.document_type,
      hasThumbnail: !!selection.thumbnail_data,
      thumbnailLength: selection.thumbnail_data?.length || 0,
      thumbnailStart: selection.thumbnail_data?.substring(0, 50) || 'none',
      processed: selection.processed,
      extracted_text: selection.extracted_text?.substring(0, 50) || 'none'
    })
    
    const isManual = selection.document_type === 'manual' || selection.pdf_id === 'manual-entry'
    const isImage = selection.document_type === 'image'
    console.log('🔍 Selection preview rendering:', {
      id: selection.id,
      document_type: selection.document_type,
      pdf_id: selection.pdf_id,
      document_id: selection.document_id,
      isManual: isManual,
      isImage: isImage,
      hasExtractedText: !!selection.extracted_text,
      extractedTextLength: selection.extracted_text?.length || 0,
      extracted_text: selection.extracted_text?.substring(0, 50) + '...',
      processed: selection.processed,
      thumbnail_data: selection.thumbnail_data ? 'present' : 'missing'
    })
    
    // Handle manual entries specially - they don't have thumbnails
    if (isManual) {
      const manualHtml = `
        <div class="selection-preview-container">
          <div class="selection-text-preview">
            <div class="text-sm text-gray-600 bg-green-50 p-3 rounded border border-green-200 max-w-full overflow-hidden">
              <div class="font-medium text-xs text-green-700 mb-2 flex items-center">
                <span class="mr-2">✍️</span>
                Manual Text Entry
              </div>
              <div class="text-gray-800 text-sm leading-relaxed whitespace-pre-wrap break-words overflow-wrap-anywhere max-h-48 overflow-y-auto">${selection.extracted_text || selection.edited_text || 'No text content'}</div>
            </div>
          </div>
        </div>
      `
      console.log('✅ Returning manual entry HTML:', manualHtml.substring(0, 200) + '...')
      return manualHtml
    }
    
    // ALWAYS prioritize thumbnail image over text content to show visual captures
    // Check if we have valid thumbnail data (including base64 data URLs)
    const hasValidThumbnail = selection.thumbnail_data && 
      selection.thumbnail_data !== 'loading' && 
      selection.thumbnail_data !== 'error' && 
      selection.thumbnail_data.length > 10 &&
      (selection.thumbnail_data.startsWith('data:image/') || selection.thumbnail_data.startsWith('blob:'))
    
    console.log('🔍 Thumbnail validation debug:', {
      id: selection.id,
      hasThumbnailData: !!selection.thumbnail_data,
      notLoading: selection.thumbnail_data !== 'loading',
      notError: selection.thumbnail_data !== 'error',
      hasLength: selection.thumbnail_data?.length > 10,
      startsWithDataImage: selection.thumbnail_data?.startsWith('data:image/'),
      startsWithBlob: selection.thumbnail_data?.startsWith('blob:'),
      hasValidThumbnail: hasValidThumbnail,
      thumbnailStart: selection.thumbnail_data?.substring(0, 30)
    })
    
    if (hasValidThumbnail) {
      // Handle different document types for display
      const pageInfo = isImage ? 'Image Selection' : `Page ${selection.page_number}`
      const documentIcon = isImage ? '🖼️' : '📄'
      
      return `
        <div class="selection-preview-container">
          <div class="selection-image-wrapper" style="position: relative;">
            <img 
              src="${selection.thumbnail_data}" 
              alt="Captured text region ${selection.id}"
              class="selection-preview-image"
              style="width: 100%; height: auto; min-height: 200px; max-height: 300px; object-fit: contain; border: 1px solid #e5e7eb; border-radius: 8px; cursor: pointer; box-shadow: 0 4px 8px rgba(0,0,0,0.1); background: #ffffff;"
              onclick="window.showImageModal('${selection.thumbnail_data}', 'Captured Description - ${pageInfo}', '${Math.round(selection.width)}×${Math.round(selection.height)}px')"
              title="Click to view full size • Captured from ${pageInfo} • ${Math.round(selection.width)}×${Math.round(selection.height)}px"
            />
            <div class="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded" style="position: absolute; top: 8px; right: 8px; background: rgba(59, 130, 246, 0.9); color: white; font-size: 11px; padding: 2px 6px; border-radius: 4px;">
              ${documentIcon} ${pageInfo}
            </div>
            <div class="absolute bottom-2 left-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded" style="position: absolute; bottom: 8px; left: 8px; background: rgba(0, 0, 0, 0.7); color: white; font-size: 11px; padding: 2px 6px; border-radius: 4px;">
              🔍 Click to enlarge
            </div>
          </div>
          <!-- OCR text section - always available but hidden in Advanced Mode -->
          <div class="mt-3" ${this.appState?.getAdvancedMode() ? 'style="display: none;"' : ''}>
            ${selection.extracted_text ? `
              <details>
                <summary class="cursor-pointer text-sm font-medium text-gray-600 hover:text-gray-800">
                  📝 View Extracted Text (${selection.extracted_text.length} chars)
                </summary>
                <div class="mt-2 text-sm text-gray-700 bg-gray-50 p-3 rounded border">
                  ${selection.extracted_text}
                </div>
              </details>
            ` : `
              <div class="text-center py-2">
                <div class="inline-flex items-center px-3 py-1 rounded-full text-xs ${selection.processed === 'processing' ? 'text-orange-600 bg-orange-50' : 'text-blue-600 bg-blue-50'}">
                  ${selection.processed === 'processing' ? '<div class="animate-spin rounded-full h-3 w-3 border-b border-orange-600 mr-2"></div>' : ''}
                  <span class="mr-1">${selection.processed === 'processing' ? '🔍' : '⏳'}</span>
                  ${selection.processed === 'processing' ? 'OCR Processing...' : 'OCR Queued...'}
                </div>
                <div class="text-xs text-gray-400 mt-1">
                  ${selection.processed === 'processing' ? 'Extracting text from image' : 'Waiting for text extraction'}
                </div>
              </div>
            `}
          </div>
          
          <!-- Advanced Mode: Hidden text section that updates silently -->
          ${this.appState?.getAdvancedMode() ? `
            <div class="mt-3" style="display: none;" data-selection-id="${selection.id}">
              ${selection.extracted_text ? `
                <details>
                  <summary class="cursor-pointer text-sm font-medium text-gray-600 hover:text-gray-800">
                    📝 View Extracted Text (${selection.extracted_text.length} chars)
                  </summary>
                  <div class="mt-2 text-sm text-gray-700 bg-gray-50 p-3 rounded border">
                    ${selection.extracted_text}
                  </div>
                </details>
              ` : `
                <div class="text-xs text-gray-400 text-center py-1">
                  ⏳ Text extraction in progress...
                </div>
              `}
            </div>
          ` : ''}
        </div>
      `
    } else if (selection.thumbnail_data === 'loading') {
      return `
        <div class="selection-preview-loading">
          <div class="flex items-center justify-center py-8 text-gray-500 bg-blue-50 rounded border-2 border-dashed border-blue-300">
            <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mr-3"></div>
            <div class="text-center">
              <div class="font-medium">Generating Preview...</div>
              <div class="text-sm text-gray-400 mt-1">Creating screenshot of selected region</div>
            </div>
          </div>
        </div>
      `
    } else if (selection.extracted_text) {
      const documentType = isImage ? 'image' : 'PDF'
      const tipText = isImage ? 'Click "Edit" to view this selection in the original image context' : 'Click "Edit" to view this selection in the original PDF context'
      
      return `
        <div class="selection-text-preview">
          <div class="text-sm text-gray-600 bg-yellow-50 p-3 rounded border border-yellow-200">
            <div class="font-medium text-xs text-yellow-700 mb-2 flex items-center">
              <span class="mr-2">⚠️</span>
              Text-Only Preview (${documentType} preview failed to generate)
            </div>
            <div class="text-gray-800 font-mono text-xs leading-relaxed">${selection.extracted_text}</div>
          </div>
          <div class="mt-2 text-xs text-orange-600 bg-orange-50 p-2 rounded border border-orange-200">
            💡 <strong>Tip:</strong> ${tipText}
          </div>
        </div>
      `
    } else {
      const pageInfo = isImage ? 'Image Selection' : `Page ${selection.page_number}`
      const documentIcon = isImage ? '🖼️' : '📄'
      
      // Check if thumbnail is being generated or OCR is processing
      const hasThumbnail = selection.thumbnail_data && selection.thumbnail_data !== 'loading' && selection.thumbnail_data !== 'error'
      const isProcessing = selection.processed === 'processing'
      const isPending = selection.processed === 'pending'
      
      let statusText = 'Processing Selection...'
      let statusSubtext = 'Image preview and text extraction in progress'
      let statusIcon = '⏳'
      
      if (isProcessing) {
        statusText = 'OCR Processing...'
        statusSubtext = 'Extracting text from image'
        statusIcon = '🔍'
      } else if (isPending && hasThumbnail) {
        statusText = 'OCR Queued...'
        statusSubtext = 'Waiting for text extraction'
        statusIcon = '⏳'
      } else if (isPending && !hasThumbnail) {
        statusText = 'Generating Preview...'
        statusSubtext = 'Creating image thumbnail'
        statusIcon = '📸'
      }
      
      return `
        <div class="selection-no-preview">
          <div class="text-center py-8 text-gray-500 bg-gray-50 rounded border-2 border-dashed border-gray-300">
            <div class="text-4xl mb-3">${documentIcon}</div>
            <div class="font-medium text-lg flex items-center justify-center">
              <span class="mr-2">${statusIcon}</span>
              ${statusText}
            </div>
            <div class="text-sm mt-2">${statusSubtext}</div>
            <div class="mt-4">
              <div class="inline-flex items-center px-3 py-1 rounded-full text-xs bg-blue-100 text-blue-700">
                ${pageInfo} • ${Math.round(selection.width)}×${Math.round(selection.height)}px
              </div>
            </div>
            ${isProcessing ? `
              <div class="mt-3">
                <div class="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">
                  <div class="animate-spin rounded-full h-3 w-3 border-b border-green-600 mr-2"></div>
                  OCR Active
                </div>
              </div>
            ` : ''}
          </div>
        </div>
      `
    }
  }
}
